import type { ConsentRegionMode } from '../../shared/privacyConsent'

const CONSENT_REGION_EVENT = 'sugar-gremlin:consent-region-change'

let mode: ConsentRegionMode = 'unknown'
let resolutionPromise: Promise<ConsentRegionMode> | null = null

export function getConsentRegionMode(): ConsentRegionMode {
  return mode
}

function setConsentRegionMode(next: ConsentRegionMode): ConsentRegionMode {
  if (mode === next) return mode
  mode = next
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent<ConsentRegionMode>(CONSENT_REGION_EVENT, { detail: mode }))
  }
  return mode
}

function qaRegionParameter(): string {
  if (typeof window === 'undefined') return ''
  const value = new URLSearchParams(window.location.search).get('qa-consent-region')
  return value === 'nz' || value === 'opt-in'
    ? `?qa-consent-region=${encodeURIComponent(value)}`
    : ''
}

export function resolveConsentRegionMode(): Promise<ConsentRegionMode> {
  if (typeof window === 'undefined') return Promise.resolve('explicit-opt-in')
  if (mode !== 'unknown') return Promise.resolve(mode)
  if (resolutionPromise) return resolutionPromise

  resolutionPromise = fetch(`/api/privacy/consent-region${qaRegionParameter()}`, {
    method: 'GET',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  })
    .then(async (response) => {
      if (!response.ok) throw new Error('consent-region-unavailable')
      const payload = await response.json() as { mode?: unknown }
      if (payload.mode !== 'nz-opt-out' && payload.mode !== 'explicit-opt-in') {
        throw new Error('consent-region-invalid')
      }
      return setConsentRegionMode(payload.mode)
    })
    .catch(() => setConsentRegionMode('explicit-opt-in'))

  return resolutionPromise
}

export function subscribeConsentRegionMode(listener: (mode: ConsentRegionMode) => void): () => void {
  if (typeof window === 'undefined') return () => undefined

  const handle = (event: Event) => {
    listener((event as CustomEvent<ConsentRegionMode>).detail)
  }
  window.addEventListener(CONSENT_REGION_EVENT, handle)

  return () => {
    window.removeEventListener(CONSENT_REGION_EVENT, handle)
  }
}
